import { css } from "@emotion/react";

import { Icon } from "@zapier/zinnia-icons";
import { Colors, Shadows, Spacer, Typography } from "@zapier/design-system";

import { Portal } from "@zapier/reporting/src/components/global/Portal";
import { badgePortalId, FilterBadges } from "../constants";

type Props = {
  children?: React.ReactNode;
  image?: React.ReactElement;
  onClear?: () => void;
  type: FilterBadges;
  value: string;
};

const disabledCss = css`
  &:hover {
    box-shadow: none;
  }
`;

const dateFilterCss = css`
  max-width: 24rem;
`;

const valueCss = css`
  ${Typography.Body1}

  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const rootCss = css`
  align-items: center;
  background-color: ${Colors.GrayWarm2};
  border: 1px ${Colors.GrayWarm6} solid;
  border-radius: 30px;
  display: grid;
  grid-auto-flow: column;
  height: 44px;
  padding: 9px 16px;

  &:hover {
    box-shadow: ${Shadows.elevation5};

    .${valueCss.name} {
      font-weight: 600;
    }
  }
`;

const titleCss = css`
  ${Typography.Body3}
`;

export function FilterChip({ children, image, onClear, type, value }: Props) {
  const hoverText = `Filtered by ${type}: ${value}. Click to clear.`;

  return onClear ? (
    <Portal portalName={badgePortalId} type={type}>
      <button css={rootCss} onClick={onClear} title={hoverText}>
        {image ? image : <span css={titleCss}>{type}:</span>}
        <Spacer width={image ? 10 : 5} />
        <span
          css={[valueCss, type === FilterBadges.dateRange && dateFilterCss]}
          data-value={value}
        >
          {children ?? value}
        </span>
        <Spacer width={16} />
        <Icon color="GrayWarm10" name="formX" size={24} />
      </button>
    </Portal>
  ) : (
    <Portal portalName={badgePortalId} type={type}>
      <span css={[rootCss, disabledCss]}>
        {image ? image : <span css={titleCss}>{type}:</span>}
        <Spacer width={5} />
        <span
          css={[valueCss, type === FilterBadges.dateRange && dateFilterCss]}
          data-value={value}
        >
          {children ?? value}
        </span>
      </span>
    </Portal>
  );
}
